import { useMemo, useReducer, useState } from "react";

export interface UseNestedListItem<T, U> {
  /**
   * ルートアイテムに渡すpropsを指定する
   */
  props: T;
  /**
   * ネストアイテムを指定する
   */
  nestedItems?: {
    props: U;
  }[];
  /**
   * ネストアイテムをルートアイテムがクリックされた時に読み込むための関数を指定する
   */
  loadNestedItems?: (props: T) => Promise<
    {
      props: U;
    }[]
  >;
}

export interface UseNestedListOptions {
  /**
   * 同時に開けるルートアイテムの数を一つに制限する
   */
  singleOpen?: boolean;
}

export const useNestedList = <
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
>(
  items: UseNestedListItem<T, U>[],
  options: UseNestedListOptions = {},
) => {
  const [indexes, setIndexes] = useReducer((prev: number[], next: number) => {
    if (options.singleOpen) {
      if (prev.includes(next)) {
        return [];
      } else {
        return [next];
      }
    } else {
      if (prev.includes(next)) {
        return prev.filter((i) => i !== next);
      } else {
        return [...prev, next];
      }
    }
  }, []);
  const [nestedItemsMap, setNestedItemsMap] = useState<
    Record<number, { props: U }[] | undefined>
  >({});

  const result = useMemo(() => {
    return items?.map((item, index) => {
      const open = indexes.includes(index);
      const nestedItems = nestedItemsMap[index] ?? item.nestedItems ?? [];
      return {
        open,
        items: open ? nestedItems : [],
        props: {
          ...item.props,
          onClick: async () => {
            /**
             * propsにonClickがある場合はそれを実行する
             */
            if (
              Object.hasOwn(item.props, "onClick") &&
              typeof item.props.onClick === "function"
            ) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call
              item.props.onClick();
            }
            if (!open) {
              const items = await item.loadNestedItems?.(item.props);
              setNestedItemsMap((prev) => {
                return {
                  ...prev,
                  [index]: items,
                };
              });
            }
            setIndexes(index);
          },
        },
      };
    });
  }, [indexes, nestedItemsMap, items]);
  return result;
};
